import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Upload, Trash2, CheckCircle, RotateCcw } from "lucide-react";

const CATEGORIES = ["Events", "Brands", "Lifestyle", "Business", "Artists", "Luxury", "Travel"];

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [tab, setTab] = useState("requests");
  const [quotes, setQuotes] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload form (portfolio)
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [uploading, setUploading] = useState(false);

  // Client logo form
  const [clientFile, setClientFile] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientUploading, setClientUploading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, pRes, cRes] = await Promise.all([
        api.get("/quotes"),
        api.get("/portfolio"),
        api.get("/clients"),
      ]);
      setQuotes(qRes.data || []);
      setPortfolio(pRes.data || []);
      setClients(cRes.data || []);
    } catch (err) {
      console.error("Admin loadAll error:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (checked && user && user.role === "admin") {
      loadAll();
    }
  }, [checked, user, loadAll]);

  if (!checked) return <div className="min-h-screen bg-black text-zinc-500 flex items-center justify-center font-mono-tight">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Updated");
    } catch {
      toast.error("Failed");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
    } catch {
      toast.error("Failed");
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      await api.post("/portfolio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setFile(null);
      setTitle("");
      loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Remove this item from portfolio?")) return;
    try {
      await api.delete(`/portfolio/${id}`);
      setPortfolio((p) => p.filter((i) => i.id !== id));
    } catch {
      toast.error("Failed");
    }
  };

  const uploadClient = async (e) => {
    e.preventDefault();
    if (!clientFile || !clientName.trim()) {
      toast.error("Provide name and logo");
      return;
    }
    setClientUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", clientFile);
      fd.append("name", clientName);
      await api.post("/clients", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Client added");
      setClientFile(null);
      setClientName("");
      const fileInput = document.querySelector('[data-testid="admin-client-file-input"]');
      if (fileInput) fileInput.value = "";
      loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setClientUploading(false);
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm("Remove this client?")) return;
    try {
      await api.delete(`/clients/${id}`);
      setClients((c) => c.filter((x) => x.id !== id));
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-10 py-5 flex items-center justify-between sticky top-0 bg-black/80 backdrop-blur-xl z-40">
        <div className="flex items-center gap-8">
          <h1 className="font-mono-tight text-white">SALSIDE ADMIN</h1>
          <nav className="flex gap-2">
            <button
              onClick={() => setTab("requests")}
              data-testid="admin-tab-requests-btn"
              className={`font-mono-tight px-4 py-2 ${tab === "requests" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
            >
              {t.admin.requests} ({quotes.length})
            </button>
            <button
              onClick={() => setTab("portfolio")}
              data-testid="admin-tab-portfolio-btn"
              className={`font-mono-tight px-4 py-2 ${tab === "portfolio" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
            >
              {t.admin.portfolio} ({portfolio.length})
            </button>
            <button
              onClick={() => setTab("clients")}
              data-testid="admin-tab-clients-btn"
              className={`font-mono-tight px-4 py-2 ${tab === "clients" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
            >
              {t.admin.clients} ({clients.length})
            </button>
          </nav>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          className="font-mono-tight text-zinc-400 hover:text-white inline-flex items-center gap-2"
        >
          <LogOut size={14} /> {t.admin.logout}
        </button>
      </header>

      <main className="px-6 md:px-10 py-10">
        {loading ? (
          <p className="text-zinc-500 font-mono-tight">Loading…</p>
        ) : tab === "requests" ? (
          quotes.length === 0 ? (
            <p className="text-zinc-500" data-testid="admin-no-requests">{t.admin.no_requests}</p>
          ) : (
            <div className="space-y-3" data-testid="admin-quotes-list">
              {quotes.map((q) => (
                <div key={q.id} className="border border-white/10 p-6 hover:bg-white/[0.02] transition-colors" data-testid={`admin-quote-${q.id}`}>
                  <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <span className="text-xl font-medium">{q.name}</span>
                        <span className="font-mono-tight text-zinc-500">·</span>
                        <span className="font-mono-tight text-zinc-400">{q.project_type}</span>
                        <span className={`font-mono-tight px-2 py-0.5 ${q.status === "done" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white"}`}>
                          {q.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-zinc-400">
                        <div><span className="text-zinc-600">Email: </span>{q.email}</div>
                        {q.phone && <div><span className="text-zinc-600">Phone: </span>{q.phone}</div>}
                        {q.company && <div><span className="text-zinc-600">Company: </span>{q.company}</div>}
                        {q.location && <div><span className="text-zinc-600">Location: </span>{q.location}</div>}
                        {q.project_date && <div><span className="text-zinc-600">Date: </span>{q.project_date}</div>}
                        {q.budget && <div><span className="text-zinc-600">Budget: </span>{q.budget}</div>}
                        {q.urgency && <div><span className="text-zinc-600">Urgency: </span>{q.urgency}</div>}
                        {q.deliverables && q.deliverables.length > 0 && (
                          <div className="md:col-span-3"><span className="text-zinc-600">Deliverables: </span>{q.deliverables.join(", ")}</div>
                        )}
                        {q.references && <div className="md:col-span-3"><span className="text-zinc-600">References: </span>{q.references}</div>}
                        {q.message && <div className="md:col-span-3 mt-2"><span className="text-zinc-600">Message: </span>{q.message}</div>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {q.status === "done" ? (
                        <button onClick={() => updateStatus(q.id, "new")} className="font-mono-tight text-zinc-400 hover:text-white inline-flex items-center gap-2" data-testid={`admin-quote-reopen-${q.id}`}>
                          <RotateCcw size={14} /> {t.admin.reopen}
                        </button>
                      ) : (
                        <button onClick={() => updateStatus(q.id, "done")} className="font-mono-tight text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-2" data-testid={`admin-quote-done-${q.id}`}>
                          <CheckCircle size={14} /> {t.admin.mark_done}
                        </button>
                      )}
                      <button onClick={() => deleteQuote(q.id)} className="font-mono-tight text-red-400 hover:text-red-300 inline-flex items-center gap-2" data-testid={`admin-quote-delete-${q.id}`}>
                        <Trash2 size={14} /> {t.admin.delete}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : tab === "portfolio" ? (
          <div>
            <form onSubmit={upload} className="border border-white/10 p-6 mb-10" data-testid="admin-upload-form">
              <p className="font-mono-tight text-zinc-500 mb-6">{t.admin.upload}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="field">
                  <input type="text" placeholder=" " value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-upload-title-input" />
                  <label>{t.admin.title_field}</label>
                </div>
                <div className="field has-value">
                  <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="admin-upload-category-select">
                    {CATEGORIES.map((c) => <option key={c} value={c} className="bg-black">{c}</option>)}
                  </select>
                  <label>{t.admin.category_field}</label>
                </div>
                <div className="field has-value">
                  <input type="file" accept="image/*,video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="admin-upload-file-input" />
                  <label>{t.admin.file}</label>
                </div>
              </div>
              <button type="submit" disabled={uploading} className="btn-primary mt-4" data-testid="admin-upload-submit-btn">
                <Upload size={14} /> {uploading ? "…" : t.admin.upload}
              </button>
            </form>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-portfolio-grid">
              {portfolio.map((p) => (
                <div key={p.id} className="relative group border border-white/10" data-testid={`admin-portfolio-${p.id}`}>
                  {p.media_type === "video" ? (
                    <video src={`${API}/files/${p.id}`} muted className="w-full aspect-square object-cover" />
                  ) : (
                    <img src={`${API}/files/${p.id}`} alt={p.title} className="w-full aspect-square object-cover" />
                  )}
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-4">
                    <div>
                      <p className="font-mono-tight text-zinc-400">{p.category}</p>
                      <p className="text-white">{p.title || "Untitled"}</p>
                    </div>
                    <button onClick={() => deleteItem(p.id)} className="font-mono-tight text-red-400 hover:text-red-300 inline-flex items-center gap-2 self-start" data-testid={`admin-portfolio-delete-${p.id}`}>
                      <Trash2 size={14} /> {t.admin.delete}
                    </button>
                  </div>
                </div>
              ))}
              {portfolio.length === 0 && <p className="text-zinc-500 col-span-full">No items yet.</p>}
            </div>
          </div>
        ) : (
          /* Clients tab */
          <div data-testid="admin-clients-tab">
            <form onSubmit={uploadClient} className="border border-white/10 p-6 mb-10" data-testid="admin-client-upload-form">
              <p className="font-mono-tight text-zinc-500 mb-6">{t.admin.upload}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="field">
                  <input
                    type="text"
                    placeholder=" "
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    data-testid="admin-client-name-input"
                  />
                  <label>{t.admin.client_name}</label>
                </div>
                <div className="field has-value">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setClientFile(e.target.files?.[0] || null)}
                    data-testid="admin-client-file-input"
                  />
                  <label>{t.admin.client_logo}</label>
                </div>
              </div>
              <button type="submit" disabled={clientUploading} className="btn-primary mt-4" data-testid="admin-client-submit-btn">
                <Upload size={14} /> {clientUploading ? "…" : t.admin.upload}
              </button>
            </form>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="admin-clients-grid">
              {clients.map((c) => (
                <div key={c.id} className="relative group border border-white/10 p-6 bg-zinc-900 flex items-center justify-center aspect-square" data-testid={`admin-client-${c.id}`}>
                  <img
                    src={`${API}/clients/${c.id}/logo`}
                    alt={c.name}
                    className="max-h-full max-w-full object-contain"
                  />
                  <div className="absolute inset-0 bg-black/85 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3 p-3">
                    <p className="text-white text-sm text-center">{c.name}</p>
                    <button onClick={() => deleteClient(c.id)} className="font-mono-tight text-red-400 hover:text-red-300 inline-flex items-center gap-2" data-testid={`admin-client-delete-${c.id}`}>
                      <Trash2 size={14} /> {t.admin.delete}
                    </button>
                  </div>
                </div>
              ))}
              {clients.length === 0 && <p className="text-zinc-500 col-span-full">No clients yet.</p>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
